"use client"

import { AppLogo } from "@/components/app-logo"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Footer } from "@/components/ui/footer"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ArrowLeft, Shield, Lock, Eye, FileText, Users, Clock, Globe, Mail } from "lucide-react"
import SmartLink from "@/components/smart-link"

export default function PrivacyPage() {
  const { t } = useLanguage()

  const sections = [
    {
      icon: FileText,
      titleKey: "privacy.intro.title",
      contentKey: "privacy.intro.content"
    },
    {
      icon: Eye,
      titleKey: "privacy.dataCollection.title",
      contentKey: "privacy.dataCollection.content"
    },
    {
      icon: Users,
      titleKey: "privacy.dataUsage.title",
      contentKey: "privacy.dataUsage.content"
    },
    {
      icon: Lock,
      titleKey: "privacy.dataSecurity.title",
      contentKey: "privacy.dataSecurity.content"
    },
    {
      icon: Shield,
      titleKey: "privacy.userRights.title",
      contentKey: "privacy.userRights.content"
    },
    {
      icon: Globe,
      titleKey: "privacy.cookies.title",
      contentKey: "privacy.cookies.content"
    },
    {
      icon: Clock,
      titleKey: "privacy.dataRetention.title",
      contentKey: "privacy.dataRetention.content"
    },
    {
      icon: Users,
      titleKey: "privacy.thirdParty.title",
      contentKey: "privacy.thirdParty.content"
    },
    {
      icon: Mail,
      titleKey: "privacy.contact.title",
      contentKey: "privacy.contact.content"
    }
  ]

  return (
    <div className="min-h-screen bg-background text-foreground p-3 md:p-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full">
        {/* Header avec retour et titre */}
        <div className="border-b mb-4 md:mb-6">
          <div className="flex h-14 md:h-16 items-center px-2 md:px-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => {
                if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
                  const pathname = window.location.pathname || '';
                  const outIdx = pathname.lastIndexOf('/out/');
                  const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
                  window.location.href = base + 'index.html';
                } else {
                  if (window.history.length > 1) window.history.back();
                  else window.location.href = '/';
                }
              }} 
              className="h-9 w-9 md:h-10 md:w-10"
            >
              <ArrowLeft className="h-5 w-5 md:h-6 md:w-6" />
            </Button>
            <div className="ml-2 md:ml-4 flex items-center gap-2">
              <AppLogo />
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight">
                {t("privacy.title")}
              </h1>
            </div>
          </div>
        </div>

        {/* Last updated */}
        <div className="mb-6 text-sm text-muted-foreground text-center">
          {t("privacy.lastUpdated")}: 7 novembre 2025
        </div>

        {/* Content sections */}
        <div className="space-y-4 md:space-y-6 mb-6">
          {sections.map((section, index) => {
            const Icon = section.icon
            return (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <Icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg md:text-xl font-semibold mb-2">
                        {t(section.titleKey)}
                      </h2>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                        {t(section.contentKey)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Navigation buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6 md:mb-8">
          <Button 
            onClick={() => {
              if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
                const pathname = window.location.pathname || '';
                const outIdx = pathname.lastIndexOf('/out/');
                const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
                window.location.href = base + 'index.html';
              } else {
                if (window.history.length > 1) window.history.back();
                else window.location.href = '/';
              }
            }} 
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("nav.back")}
          </Button>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button variant="outline" asChild className="w-full sm:w-auto">
              <SmartLink href="/terms/">
                {t("nav.terms")}
              </SmartLink>
            </Button>
            <Button variant="outline" asChild className="w-full sm:w-auto">
              <SmartLink href="/about/">
                {t("nav.about")}
              </SmartLink>
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

